/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";

// owner
import geobeaconClient from "@apis/geobeaconClient";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";
import InfraMarkerIcon, {
  infraIconTooltipOffset,
} from "@/components/Leaflet/Map/Marker/InfraMarkerIcon";

interface InfraBeacon {
  id: number;
  name: string;
  serial: string;
  address?: string;
  location?: string;
  currentLat: number;
  currentLng: number;
  status?: string;
  lastSeenAlive?: string;
  coordinate?: {
    lat: number;
    lon: number;
    lastSeen?: string;
  };
}

interface InfraLayerOptions {
  ignoreInfraId?: number;
  query?: Array<{
    filterKey: string;
    categoryId?: string;
    filterType?: string;
    searchType?: string;
    notFilter?: boolean;
    filterValue?: unknown;
  }>;
}

interface SearchQuery {
  searchQueryType: string;
  queryObjectType: string;
  query: Array<{
    filterKey: string;
    categoryId?: string;
    filterType?: string;
    searchType?: string;
    notFilter?: boolean;
    filterValue?: unknown;
  }>;
  requestedTableColumns: Array<{
    columnKey: string;
    order: null;
  }>;
  selectedOrgId?: number;
}

const InfraLayerClass = (L.FeatureGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,

  updateData: function (orgId: number | null, options: InfraLayerOptions) {
    const self = this;
    const pruneCluster = new PruneClusterForLeaflet(10, 10);
    self.pruneCluster = pruneCluster;
    PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;

    pruneCluster.BuildLeafletClusterIcon = function (cluster: any) {
      const _length = cluster.population;
      let width = 24,
        textSize = 11;
      if (_length >= 100 && _length < 1000) {
        width = 36;
        textSize = 7;
      } else if (_length >= 1000) {
        width = 44;
        textSize = 6;
      }

      const svgString = `<svg width="${width}" height="${width}" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path id="cluster-bg" d="M6 0.5H12H18H20C21.933 0.5 23.5 2.067 23.5 4V6V12V18V20C23.5 21.933 21.933 23.5 20 23.5H18H12H6H4C2.067 23.5 0.5 21.933 0.5 20V18V12V6V4C0.5 2.067 2.067 0.5 4 0.5H6Z"
                  fill="#A900D5"/>
            <path id="cluster-fg" d="M4 2.5H12H20C20.8284 2.5 21.5 3.17157 21.5 4V7V12V20C21.5 20.8284 20.8284 21.5 20 21.5H17H12H7H4C3.17157 21.5 2.5 20.8284 2.5 20V12V7V4C2.5 3.17157 3.17157 2.5 4 2.5Z"
                  stroke="#FFFFFF"/>
            <text id="cluster-text" x="50%" y="50%" font-size="${textSize}" font-weight="bold" transform="translate(0, 3.5)" text-anchor="middle" fill="#ffffff">${_length}</text>
          </svg>
      `;
      return ClusterIcon({
        count: _length,
        svgString: svgString,
        className: "cluster-svg-infras",
      }) as any;
    };

    pruneCluster.PrepareLeafletMarker = function (leafletMarker: any, data: any) {
      data.icon.setMarker(leafletMarker);
      leafletMarker.setIcon(data.icon);
      if (data.tooltip) {
        leafletMarker.unbindTooltip();
        leafletMarker.bindTooltip(data.tooltip, {
          permanent: true,
          direction: "top",
          className: "infra-leaflet-tooltip",
          offset: infraIconTooltipOffset(),
        });
      }
    };

    const basic: SearchQuery = {
      searchQueryType: "BASIC",
      queryObjectType: "SIGFOX_DEVICE",
      query: [],
      requestedTableColumns: [
        {
          columnKey: "name",
          order: null,
        },
        {
          columnKey: "serial",
          order: null,
        },
        {
          columnKey: "address",
          order: null,
        },
        {
          columnKey: "location",
          order: null,
        },
        {
          columnKey: "currentLat",
          order: null,
        },
        {
          columnKey: "currentLng",
          order: null,
        },
        {
          columnKey: "status",
          order: null,
        },
      ],
    };

    if (orgId) {
      basic.selectedOrgId = orgId;
    }

    if (options.query) {
      basic.query = options.query.filter(
        (item) =>
          item.filterKey === "organizations" || item.filterKey === "partners"
      );
    }

    self.bound = L.latLngBounds([]);
    self.clearLayers();

    self.addLayer(self.pruneCluster);

    void geobeaconClient.search(basic as any).then(({ data }: any) => {
      self.processData(data.data, options);
    });
  },

  transformData: function (beacon: InfraBeacon) {
    beacon.coordinate = {
      lat: beacon.currentLat,
      lon: beacon.currentLng,
      lastSeen: beacon.lastSeenAlive,
    };
  },

  isValidCoordinate: function (
    coordinate: { lat: number; lon: number } | undefined
  ): boolean {
    return !!(coordinate && (coordinate.lat || coordinate.lon));
  },

  processData: function (data: InfraBeacon[], options: InfraLayerOptions) {
    const self = this;
    const devices: InfraBeacon[] = [];
    data.forEach((obj: InfraBeacon) => {
      if (options && options.ignoreInfraId != null && +options.ignoreInfraId === obj.id) return;
      self.transformData(obj);
      if (self.isValidCoordinate(obj.coordinate)) {
        devices.push(obj);
      }
    });
    self.processMarkers(devices);
  },

  processMarkers: function (devices: InfraBeacon[]) {
    const self = this;

    // register the remaining markers
    devices.forEach((beacon: InfraBeacon) => {
      // new marker, add to cluster
      const coordinate = beacon.coordinate;
      if (!coordinate) return;
      const icon = InfraMarkerIcon(beacon as any);

      const marker = new PruneCluster.Marker(
        coordinate.lat || 0,
        coordinate.lon || 0,
        {
          icon: icon,
          tooltip: beacon.name,
        }
      );
      marker.data.beaconInfo = beacon;
      (marker as any).getLatLng = function () {
        return new L.LatLng(this.position.lat, this.position.lon);
      };

      self.bound.extend(L.latLng(coordinate.lat, coordinate.lon));
      self.pruneCluster.RegisterMarker(marker);
    });

    self.pruneCluster.ProcessView();
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).InfraLayer = InfraLayerClass;
(L as any).infraLayer = function (opts?: L.LayerOptions): any {
  return new InfraLayerClass(opts);
};

export const InfraLayer = (L as any).infraLayer;
